import type {TravelType} from '@/modules/common/context/QueryParams/models';
import type {SearchFormValues} from '@travel-frontend/ui-components';

import {usePrevious} from '@react-hookz/web';
import {useBreakpointUp} from '@travel-frontend/core';
import {Drawer, SearchForm} from '@travel-frontend/ui-components';
import {isEqual, mapValues, noop, pick} from 'lodash';
import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {useNavigate, useParams} from 'react-router';

import {isHotelKeyword} from '@/api/keywordApi/utils';
import {SearchBar as SearchBarUi} from '@/components/SearchBar';
import {PAGE_HOTELS, PAGE_OFFERS} from '@/consts';
import {ControlBar} from '@/modules/common/components/ControlBar';
import {Sticky} from '@/modules/common/components/SearchBar/components/Sticky';
import {useSearchForm} from '@/modules/common/components/SearchBar/hooks/useSearchForm';
import {useSearchFormPersistentValues} from '@/modules/common/components/SearchBar/hooks/useSearchFormPersistentValues';
import {mapFormValuesToSearchParams} from '@/modules/common/components/SearchBar/utils';
import {useQueryParams} from '@/modules/common/context/QueryParams';
import {TravelTypes} from '@/modules/common/context/QueryParams/models';
import {
  SEA_CAMPAIGN_URL_PARAM,
  useSeaCampaign,
} from '@/modules/common/context/SeaCampaign';
import {useSearchFormOverlay} from '@/modules/common/context/SearchFormOverlay';
import {useLocalSplit} from '@/modules/common/hooks';
import {useCurrentPage} from '@/modules/common/hooks/useCurrentPage';
import {useLinkBuilder} from '@/modules/common/hooks/useLinkBuilder';
import {
  fromGiataToKeyword,
  fromKeywordToGiata,
} from '@/modules/common/utils/hotelIdFormat';
import {scrollToOfferlistIfPossible} from '@/modules/offers/utils/viewOffset';

type Props = {
  overlayOpen: boolean;
  alternativeLayout?: boolean;
  activeFilterCount?: number;
  stickyOffset?: number;
  onSubmit?: () => void;
  tabRowEnabled?: boolean;
};

const searchFormBehaviorBySplitTreatment = {
  control: 'explicitSubmit',
  A: 'explicitSubmit',
  B: 'instantSearch',
  C: 'instantSearchNoCTA',
} as const;

const SearchBarComponent = forwardRef<HTMLDivElement, Props>(
  function MemoizedSearchBar(
    {
      overlayOpen = false,
      alternativeLayout = false,
      activeFilterCount = 0,
      stickyOffset = 0,
      onSubmit = noop,
      tabRowEnabled,
    },
    parentRef
  ) {
    const {closeSearchFormOverlay} = useSearchFormOverlay();
    const lgUp = useBreakpointUp('lg');
    const currentPage = useCurrentPage();
    const seaCampaign = useSeaCampaign();
    const {buildHotelListLink, buildOfferListLink} = useLinkBuilder();
    const navigate = useNavigate();
    const [queryParams, setQueryParams] = useQueryParams();
    const pathParams = useParams();

    const showAirportSuggestion =
      useLocalSplit<'default' | 'controlgroup' | 'variation'>(
        'FSE_3458_show_airport_suggestions'
      ).treatment === 'variation';

    const searchFormBehaviorSplit = useLocalSplit<
      keyof typeof searchFormBehaviorBySplitTreatment
    >('FFP_4073_searchform_behavior');
    const searchFormBehavior = searchFormBehaviorSplit.ready
      ? searchFormBehaviorBySplitTreatment[searchFormBehaviorSplit.treatment]
      : searchFormBehaviorBySplitTreatment.A;

    const promoHotelSplitEnabled =
      useLocalSplit('FFP_4523_promo_hotellist').treatment === 'on';
    const allowEmptySearchForm =
      useLocalSplit<'variation' | 'controlgroup'>(
        'FSE_3513_allow_empty_searchform_submit'
      ).treatment === 'variation';

    const [localTravelType, setLocalTravelType] = useState<TravelType>(
      queryParams.travelType
    );

    const originalDestinationId = queryParams.destinationId;
    const displayedDestinationId = pathParams.hotelId
      ? fromGiataToKeyword(pathParams.hotelId)
      : originalDestinationId;
    const persistentFormValues = useSearchFormPersistentValues({
      searchParams: queryParams,
      destinationId: displayedDestinationId,
    });

    const persistentFormValuesPrevious = usePrevious(persistentFormValues);

    const submitSearchForm = useCallback(
      ({
        prevValues,
        nextValues,
        travelType,
      }: {
        prevValues: SearchFormValues;
        nextValues: SearchFormValues;
        travelType: TravelType;
      }) => {
        const destinationChanged =
          nextValues.destination?.id !== prevValues.destination?.id;
        const hotelSelected = isHotelKeyword(nextValues.destination?.id ?? '');

        const nextSearchParams = {
          ...mapFormValuesToSearchParams(nextValues),
          travelType,
        };

        // https://invia.atlassian.net/browse/FFP-4285
        if (!destinationChanged && hotelSelected) {
          nextSearchParams.destinationId = originalDestinationId;
        }

        if (destinationChanged && hotelSelected) {
          if (currentPage === PAGE_OFFERS) {
            window.scrollTo(0, 0);
          }
          // https://invia.atlassian.net/browse/FFP-4523
          if (currentPage === PAGE_HOTELS && promoHotelSplitEnabled) {
            const replace = !seaCampaign.active;
            const hotelListLink = buildHotelListLink({
              getFilterParams: () => null,
              // @ts-expect-error ...
              getSearchParams: () => nextSearchParams,
              additionalState: {
                changedBy: replace ? 'search' : undefined,
              },
            });

            navigate(hotelListLink.to, {
              ...hotelListLink.options,
              replace,
            });

            return;
          }

          // @ts-expect-error need to update toolkit type
          const hotelId = fromKeywordToGiata(nextValues.destination?.id);
          const hotelDestinationId: string =
            // @ts-expect-error need to update toolkit type
            nextValues.destination?.meta?.parentId;

          const replace = currentPage === PAGE_OFFERS;
          const offerListLink = buildOfferListLink({
            hotelId,
            getFilterParams: () => null,
            getSortParams: (previousSortParams) =>
              currentPage === PAGE_HOTELS ? null : previousSortParams,
            // @ts-expect-error ...
            getSearchParams: () => ({
              ...nextSearchParams,
              destinationId: hotelDestinationId,
            }),
            additionalState: {
              changedBy: replace ? 'search' : undefined,
            },
          });

          navigate(offerListLink.to, {
            ...offerListLink.options,
            replace,
          });
        } else if (destinationChanged) {
          const replace = currentPage === PAGE_HOTELS && !seaCampaign.active;
          const hotelListLink = buildHotelListLink({
            getFilterParams: () => null,
            getSortParams: (previousSortParams) =>
              currentPage === PAGE_OFFERS ? null : previousSortParams,
            additionalState: {
              // @FIXME dirty workaround for our convoluted fetching inside effects logic...
              forceReload: currentPage === PAGE_OFFERS ? true : undefined,
              changedBy: replace ? 'search' : undefined,
            },
            // @ts-expect-error ...
            getSearchParams: () => nextSearchParams,
          });

          navigate(hotelListLink.to, {
            ...hotelListLink.options,
            replace,
          });
        }
        // it will be only called when !destinationChanged
        else {
          scrollToOfferlistIfPossible();
          setQueryParams(
            (previous) => ({
              ...previous,
              ...nextSearchParams,
              filterPriceChart: null,
              [SEA_CAMPAIGN_URL_PARAM]: null,
              destinationIdFilter: null, // Reset region filter
            }),
            {
              replace: !seaCampaign.active,
              state: {
                changedBy: 'search',
              },
            }
          );
        }
      },
      [
        buildHotelListLink,
        buildOfferListLink,
        currentPage,
        navigate,
        setQueryParams,
        seaCampaign.active,
        originalDestinationId,
        promoHotelSplitEnabled,
      ]
    );

    const mapSearchFormData = (state: any): SearchFormValues => {
      // @FIXME There must be a better solution...
      // Why does the form state contain values for fields that are disabled via config?
      // They should never have a value to begin with...
      return mapValues(
        pick(
          state.values,
          // form state may contain more fields then what we are persisting (travelPeriodNew for example)
          Object.keys(persistentFormValues)
        ),
        (value, key) => {
          // there are different travellers fields depending on searchform config.multiroom.
          // since multiroom is the default now, "travellers" can be considered deprecated,
          // so we never include it.
          if (key === 'travellers') {
            return null;
          }
          return value;
        }
      ) as SearchFormValues;
    };

    const searchFormInstance = useSearchForm({
      travelType: localTravelType,
      prevValues: persistentFormValues,
      // @ts-expect-error ...
      config: useMemo(
        () => ({
          submitButtonVisible:
            !lgUp || searchFormBehavior !== 'instantSearchNoCTA',
          submitButtonLabel:
            !lgUp &&
            (searchFormBehavior === 'instantSearch' ||
              searchFormBehavior === 'instantSearchNoCTA')
              ? 'showResults'
              : 'adjustSearch',
          airportsField: {
            showSuggestions: showAirportSuggestion,
          },
          travelTypeField: {
            mode: alternativeLayout ? 'denseList' : 'selectLight',
          },
          layout: alternativeLayout ? 'sidebar' : 'full',
        }),
        [showAirportSuggestion, lgUp, searchFormBehavior, alternativeLayout]
      ),
      onSubmit: (state: any) => {
        const currentFormValues = mapSearchFormData(state);

        const isRegionFilterApplied =
          queryParams.destinationIdFilter &&
          queryParams.destinationIdFilter !== queryParams.destinationId;

        // When `<RegionSelect />` is applied, search form should become submittable even though the
        // destination might not have changed, hence the `destinationIdFilter` check
        const didValuesChange =
          !isEqual(persistentFormValues, currentFormValues) ||
          isRegionFilterApplied;

        if (allowEmptySearchForm && !currentFormValues.destination) {
          window.location.href =
            localTravelType === TravelTypes.ownArrival
              ? '/inspiration-hotels'
              : '/inspiration-pauschalreisen';
          return;
        }

        if (
          !didValuesChange || // just clicked the button without changing anything
          searchFormBehavior === 'instantSearch' ||
          searchFormBehavior === 'instantSearchNoCTA'
        ) {
          closeSearchFormOverlay();
          return;
        }

        if (
          // a form with invalid data shouldn't trigger a search
          state.invalid ||
          (!didValuesChange &&
            // https://invia.atlassian.net/browse/FFP-3807
            queryParams.travelType === state.travelType)
        ) {
          return;
        }

        onSubmit();
        closeSearchFormOverlay();
        submitSearchForm({
          prevValues: persistentFormValues,
          nextValues: currentFormValues,
          travelType: state.travelType,
        });
      },
      onValueChange: ({field, value}: any) => {
        // ...
        if (field === 'travelType') {
          setLocalTravelType(value);
          // @FIXME
          searchFormInstance.setValue('destination', null);
          return;
        }

        if (
          searchFormBehavior === 'instantSearch' ||
          searchFormBehavior === 'instantSearchNoCTA'
        ) {
          onSubmit();
          submitSearchForm({
            prevValues: persistentFormValues,
            nextValues: {...persistentFormValues, [field]: value},
            travelType: localTravelType,
          });
        }
      },
      onClickLastSearchesItem: (state: any) => {
        const currentFormValues = mapSearchFormData(state);
        setLocalTravelType(state.travelType);
        searchFormInstance.setValue('travelType', state.travelType);
        onSubmit();
        closeSearchFormOverlay();
        submitSearchForm({
          prevValues: persistentFormValues,
          nextValues: currentFormValues,
          travelType: state.travelType,
        });
      },
    });

    // Makes sure search form values always reflect the persisted values
    useEffect(() => {
      // Prevent overwriting field values selected in the UI but not yet committed to the path
      // with the ones that are already present there when the form is submitted to avoid flickering
      if (isEqual(persistentFormValues, persistentFormValuesPrevious)) {
        return;
      }

      searchFormInstance.setValues(persistentFormValues);
      // eslint-disable-next-line react-hooks/exhaustive-deps -- keep as there might be unwanted side-effects
    }, [persistentFormValues]);

    useEffect(() => {
      if (lgUp) {
        closeSearchFormOverlay();
      }
    }, [lgUp, closeSearchFormOverlay]);

    const searchForm = alternativeLayout ? (
      <SearchForm {...searchFormInstance.register()} />
    ) : (
      <SearchBarUi onClose={closeSearchFormOverlay}>
        <SearchForm {...searchFormInstance.register()} />
      </SearchBarUi>
    );

    return (
      <>
        {!lgUp && (
          <>
            <Sticky stickyOffset={stickyOffset} ref={parentRef}>
              <ControlBar
                travelType={localTravelType}
                activeFilterCount={activeFilterCount}
                tabRowEnabled={tabRowEnabled}
              />
            </Sticky>
            <Drawer
              open={overlayOpen}
              anchor="bottom"
              onClose={closeSearchFormOverlay}
            >
              {searchForm}
            </Drawer>
          </>
        )}
        {alternativeLayout && searchForm}
        {lgUp && !alternativeLayout && (
          <Sticky stickyOffset={stickyOffset} ref={parentRef}>
            {searchForm}
          </Sticky>
        )}
      </>
    );
  }
);

export const SearchBar = memo(SearchBarComponent);
